import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinRoom, roomExists } from '../services/roomService';
import { unformatRoomCode, isValidRoomCode } from '../utils/codeGenerator';
import '../styles/JoinRoom.css';

const JoinRoom = ({ user }) => {
  const [roomCode, setRoomCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    // Convert to uppercase and limit to 7 characters (6 + hyphen)
    const value = e.target.value.toUpperCase();

    // Auto-format: add hyphen after 3 characters
    let formatted = value.replace(/[^A-Z0-9-]/g, ''); // Remove invalid chars

    if (formatted.length > 3 && formatted[3] !== '-') {
      formatted = formatted.slice(0, 3) + '-' + formatted.slice(3);
    }

    // Limit to 7 characters (ABC-123)
    formatted = formatted.slice(0, 7);

    setRoomCode(formatted);
    setError(null);
  };

  const handleJoinRoom = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Remove formatting and validate
      const cleanCode = unformatRoomCode(roomCode);

      if (!isValidRoomCode(cleanCode)) {
        setError('Invalid room code format. Code should be 6 characters.');
        setLoading(false);
        return;
      }

      // Check if room exists
      const exists = await roomExists(cleanCode);
      if (!exists) {
        setError('Room not found. Please check the code and try again.');
        setLoading(false);
        return;
      }

      // Try to join the room
      await joinRoom(
        cleanCode,
        user.uid,
        user.displayName,
        user.photoURL
      );

      // Navigate to the room lobby
      navigate(`/room/${cleanCode}`);
    } catch (err) {
      console.error('Failed to join room:', err);

      // Handle specific error messages
      if (err.message === 'Room is full') {
        setError('This room is full. Please try a different room.');
      } else if (err.message === 'Room is not available') {
        setError('This room is not available. The game may have already started.');
      } else {
        setError(err.message || 'Failed to join room. Please try again.');
      }

      setLoading(false);
    }
  };

  return (
    <div className="join-room-container">
      <div className="join-room-card">
        <h2>Join a Game Room</h2>
        <p className="join-room-description">
          Enter the room code shared by your friend
        </p>

        <form onSubmit={handleJoinRoom}>
          <div className="input-group">
            <label htmlFor="roomCode">Room Code</label>
            <input
              id="roomCode"
              type="text"
              className="room-code-input"
              value={roomCode}
              onChange={handleInputChange}
              placeholder="ABC-123"
              maxLength={7}
              autoComplete="off"
              autoFocus
              disabled={loading}
            />
            <p className="input-hint">Enter the 6-character code (e.g., ABC-123)</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="join-room-button"
            disabled={loading || roomCode.length < 6}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Joining Room...
              </>
            ) : (
              <>
                <span className="icon">🚪</span>
                Join Room
              </>
            )}
          </button>

          <button
            type="button"
            className="join-back-button"
            onClick={() => navigate('/profile')}
            disabled={loading}
          >
            Back to Profile
          </button>
        </form>

        <div className="info-section">
          <h3>Need help?</h3>
          <ul className="help-list">
            <li>Room codes are 6 characters long (letters and numbers)</li>
            <li>Make sure your friend has created a room first</li>
            <li>Room codes are case-insensitive</li>
            <li>The room must be waiting for players to join</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JoinRoom;
